import { type CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useMatches } from "react-router";

import { type AccessRule, allows } from "@/auth/access";
import { useAuth } from "@/auth/use-auth";
import { AppHeader } from "@/components/AppHeader";
import { BOTTOM_NAV_HEIGHT, BottomNav } from "@/components/BottomNav";
import { ForbiddenState } from "@/components/ForbiddenState";
import { SearchProvider } from "@/features/search/SearchProvider";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ShopSwitchBanners } from "@/components/ShopSwitchBanners";
import { SectionTabs, Sidebar } from "@/components/Sidebar";
import { UsabilityPanel } from "@/dev/usability/UsabilityPanel";
import { ComingSoonPage } from "@/lib/flags/ComingSoonPage";
import type { FeatureFlag } from "@/lib/flags/flags";
import { useFeatureFlags } from "@/lib/flags/useFeatureFlags";
import { useViewport } from "@/lib/viewport";

import { filterNav } from "../nav";
import { NavBadgesProvider } from "../navBadges";

/** Route `handle` (T11-01): who may open the screen and its title key. */
export interface RouteHandle {
  access?: AccessRule;
  titleKey?: string;
  /**
   * Phase-gated screen (R18, T11-09): while the flag is off the address shows the "coming soon"
   * page (never a 404 / an error), and the screen's code is not loaded.
   */
  feature?: FeatureFlag | readonly FeatureFlag[];
}

function isRouteHandle(value: unknown): value is RouteHandle {
  return (
    typeof value === "object" &&
    value !== null &&
    ("access" in value || "titleKey" in value || "feature" in value)
  );
}

function featuresOf(handle: RouteHandle): readonly FeatureFlag[] {
  if (!handle.feature) {
    return [];
  }
  return typeof handle.feature === "string" ? [handle.feature] : handle.feature;
}

/**
 * Renders the matched screen only when every matched route's `handle.access` allows it —
 * a URL typed by hand for a hidden menu entry gets the plain "no access" page (T11-01).
 */
export function AccessOutlet() {
  const matches = useMatches();
  const { can } = useAuth();
  const flags = useFeatureFlags();
  const handles = matches.map((match) => match.handle).filter(isRouteHandle);
  const blocked = handles.some((handle) => !allows(handle.access, can));
  if (blocked) {
    return <ForbiddenState />;
  }
  const gated = handles.find((handle) => featuresOf(handle).some((flag) => !flags[flag]));
  if (gated) {
    return (
      <ComingSoonPage
        features={featuresOf(gated).filter((flag) => !flags[flag])}
        titleKey={gated.titleKey}
      />
    );
  }
  return <Outlet />;
}

/**
 * The window title follows the screen ("Orders — Shop admin", WCAG 2.4.2) and a screen reader
 * hears the new page's name after an in-app navigation (the page does not reload, T11-48).
 */
function PageTitle() {
  const { t } = useTranslation();
  const matches = useMatches();
  const { pathname } = useLocation();
  const titleKey = matches
    .map((match) => match.handle)
    .filter(isRouteHandle)
    .map((handle) => handle.titleKey)
    .filter((key): key is string => Boolean(key))
    .at(-1);
  const title = titleKey ? `${t(titleKey)} — ${t("app.name")}` : t("app.name");
  const [announced, setAnnounced] = useState("");
  const first = useRef(true);
  useEffect(() => {
    document.title = title;
  }, [title]);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setAnnounced(title);
  }, [pathname, title]);
  return (
    <p className="sr-only" aria-live="polite" data-slot="route-announcer">
      {announced}
    </p>
  );
}

/**
 * Admin shell (T11-01, design/04 §1): ≥ 1280 px sidebar; 768–1279 icon rail + bottom bar with 5
 * buttons (+ the group's entries as tabs); < 768 bottom bar + thin header. No business logic here.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const viewport = useViewport();
  const groups = filterNav(can);
  const withBottomNav = viewport !== "desktop";
  const style = withBottomNav
    ? ({ "--admin-bottom-nav": BOTTOM_NAV_HEIGHT } as CSSProperties)
    : undefined;

  return (
    <SearchProvider>
      <NavBadgesProvider>
        <div className="flex min-h-dvh flex-col" style={style} data-viewport={viewport}>
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[80] focus:rounded focus:bg-background focus:px-3 focus:py-2"
          >
            {t("app.skip_to_content")}
          </a>
          <PageTitle />
          <AppHeader compact={viewport === "phone"} />
          <OfflineBanner />
          <ShopSwitchBanners />
          <div className="flex min-h-0 flex-1">
            {viewport === "desktop" ? <Sidebar groups={groups} mode="full" /> : null}
            {viewport === "tablet" ? <Sidebar groups={groups} mode="rail" /> : null}
            <main
              id="main"
              tabIndex={-1}
              className="min-w-0 flex-1 px-4 py-4 outline-none sm:px-6 sm:py-6 xl:px-8"
              style={
                withBottomNav ? { paddingBottom: `calc(${BOTTOM_NAV_HEIGHT} + 1.5rem)` } : undefined
              }
            >
              {withBottomNav ? <SectionTabs groups={groups} /> : null}
              <AccessOutlet />
            </main>
          </div>
          {withBottomNav ? <BottomNav groups={groups} /> : null}
          {/* T11-50: the observer's recorder, only with ?usability=on */}
          <UsabilityPanel />
        </div>
      </NavBadgesProvider>
    </SearchProvider>
  );
}
